import type Company from '@/types/Company'
import type Address from '@/types/Address'
import type Headquarters from '@/types/Headquarters'
import type SubjectOfBusiness from '@/types/SubjectOfBusiness'

const PERMANENT_RESIDENCE_PACKAGES = [
  'Základný trvalý pobyt v Bratislave',
  'Najlepší trvalý pobyt v Bratislave',
] as const

const NON_PERMANENT_PACKAGES = ['Mini', 'Ideal', 'Premium', 'Extra', 'Individual'] as const

const COMPANY_TYPE_LABELS: Record<string, string> = {
  sro: 's.r.o.',
  zivnost: 'Živnosť',
  fyzos: 'Fyzická osoba',
}

export type VhqPackageState = 'active' | 'expired' | 'deactivated'

export const VHQ_PACKAGES: { name: string; permanentResidence: boolean }[] = [
  { name: 'Mini', permanentResidence: false },
  { name: 'Ideal', permanentResidence: false },
  { name: 'Premium', permanentResidence: false },
  { name: 'Extra', permanentResidence: false },
  { name: 'Individual', permanentResidence: false },
  { name: 'Základný trvalý pobyt v Bratislave', permanentResidence: true },
  { name: 'Najlepší trvalý pobyt v Bratislave', permanentResidence: true },
]

export function filterVhqPackages(companyType: number): string[] {
  const permanent = companyType === 3
  if (permanent) {
    return PERMANENT_RESIDENCE_PACKAGES.slice()
  }
  return NON_PERMANENT_PACKAGES.slice()
}

export function vhqPackageOptions(
  companyType: number,
  current?: string | null,
): string[] {
  const options = filterVhqPackages(companyType)
  if (current && !options.includes(current)) {
    options.push(current)
  }
  return options
}

export function companyTypeLabel(
  typeName: string | undefined,
  description?: string | null,
): string {
  if (!typeName) return description ?? '—'
  const slug = COMPANY_TYPE_LABELS[typeName]
  if (slug) return slug
  return description ?? typeName
}

export function vhqPackageState(
  paidUntil: string | null | undefined,
  deactivated: boolean,
  today?: Date,
): VhqPackageState {
  if (deactivated) return 'deactivated'
  const now = today ?? new Date()
  if (!paidUntil) return 'expired'
  const end = new Date(paidUntil)
  if (Number.isNaN(end.getTime())) return 'expired'
  end.setHours(23, 59, 59, 999)
  if (end.getTime() < now.getTime()) return 'expired'
  return 'active'
}

export function companyTypeFlags(type: number) {
  return {
    isSro: type === 1,
    isZivnost: type === 2,
    isFyzos: type === 3,
  }
}

export function hasLegacyCompanyData(company: Company): boolean {
  return Boolean(
    company.imanie_vyska > 0 ||
      company.imanie_splatene > 0 ||
      company.sposob_konania_konatelov > 0 ||
      company.skk_details !== '' ||
      company.konecny_uzivatelia_vyhod > 0 ||
      company.kuv_details !== '',
  )
}

export function parseSubjects(raw: string | null | undefined): SubjectOfBusiness[] {
  if (!raw) return []
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []
  return parsed as SubjectOfBusiness[]
}

export function buildCompanyPayload(company: Company): Record<string, unknown> {
  return {
    name: company.name,
    type: company.type,
    status: company.status,
    sub_status: company.sub_status,
    ico: company.ico,
    dic: company.dic,
    icdph: company.icdph,
    imanie_vyska: company.imanie_vyska,
    imanie_splatene: company.imanie_splatene,
    headquarters_id: company.headquarters_id,
    is_dph: company.is_dph,
    zaciatok_opravnenia: company.zaciatok_opravnenia,
    konecny_uzivatelia_vyhod: company.konecny_uzivatelia_vyhod,
    kuv_details: company.kuv_details,
    sposob_konania_konatelov: company.sposob_konania_konatelov,
    skk_details: company.skk_details,
    owner: company.owner,
    last_step: company.last_step,
    note: company.note,
    is_virtual: company.is_virtual,
    vhq_renewal_fakturacne_udaje_id: company.vhq_renewal_fakturacne_udaje_id,
    documents_renewal_fakturacne_udaje_id: company.documents_renewal_fakturacne_udaje_id,
    fakturacia_zaplatene_do: company.fakturacia_zaplatene_do,
    fakturacia_free: company.fakturacia_free,
    doc_template_id: company.doc_template_id,
    doc_sncounters_id: company.doc_sncounters_id,
    logo: company.logo,
    first_name: company.first_name,
    last_name: company.last_name,
    vhq_name: company.vhq_name,
    hide_from_ui: company.hide_from_ui,
  }
}

export function buildAddressPayload(address: Address): Record<string, unknown> {
  return {
    street: address.street,
    street_number: address.street_number,
    street_number2: address.street_number2,
    city: address.city,
    psc: address.psc,
    country: address.country,
  }
}

export function buildHeadquartersPayload(
  headquarters: Headquarters,
): Record<string, unknown> {
  return {
    owner_name: headquarters.owner_name,
    headquarters_type: headquarters.headquarters_type,
    is_virtual: headquarters.is_virtual,
    address_id: headquarters.address_id,
    img: headquarters.img,
  }
}

export function buildSubjectsPayload(
  subjects: SubjectOfBusiness[],
): { subjects_of_business: string } {
  const normalized = subjects.map(subject => ({
    ...subject,
    ui_category_id: subject.ui_category_id ?? 0,
  }))
  return { subjects_of_business: JSON.stringify(normalized) }
}
